import { useState, useEffect } from 'react';
import { StyledForm } from 'components/Formulario/styles';
import FormularioUtils from 'utils/formulario';
import { LoadingType } from 'components/Formulario/Components/PortalSelect';
import { NotificationManager } from 'react-notifications';
import { MessagesDefaultTimeout } from 'constants/messagesDefaultTimeout';
import { createSelectGroupInput, createSelectInput } from 'utils/FormularioHelper';
import { maplicencasAdicionaisPorDireitoParaGerenciadorDeLicencas, mapPlanosPorDireitoParaGerenciadorDeLicencas } from 'services/mappers/LicencasMapper';
import { useAuth } from 'context/AuthContext';
import ListasPrecoService from 'services/ListasPrecoService';
import ProdutosService from 'services/ProdutosService';

const CAMPO_PLANO_EMAIL = 'planoEmail';
const CAMPO_LICENCAS_OPCIONAIS = 'licencasOpcionais';
const MENSAGEM_ERRO_VALIDACAO = 'Erro ao validar disponibilidade do plano. Favor entre em contato com nosso atendimento.';
const MENSAGEM_ERRO_DADOS_NAO_OBTIDOS = 'Não foi possível obter os dados necessários.';

export const useGerenciadorDeLicencas = ({
  planoInicial = null,
  licencasIniciais = [],
  quantidadePlanoSolicitada = 1
}) => {
  const [formReference] = StyledForm.useForm();

  const [planoSelecionado, setPlanoSelecionado] = useState();
  const [licencasSelecionadas, setLicencasSelecionadas] = useState(licencasIniciais);
  const [planoDisponivel, setPlanoDisponivel] = useState(false);

  const [inputsData, setInputsData] = useState([]);
  const [planosEmail, setPlanosEmail] = useState([]);
  const [licencasOpcionais, setLicencasOpcionais] = useState([]);
  const {tenant, direito} = useAuth();
  const [dadosLicencasCarregados, setDadosLicencasCarregados] = useState(false);
  
  useEffect(() => {
    const carregarDadosParaCalculo = async () => {
      var tenantId = direito.axtTenantId;
      var direitoId = direito.entitlementId;
      var modeloDeOfertaCodigo = tenant.modeloOferta;
      Promise.all([
        ListasPrecoService.ListarPlanosPorDireito(tenantId, direitoId, modeloDeOfertaCodigo),
        ListasPrecoService.ListarLicencasAdicionaisPorDireito(tenantId, direitoId, modeloDeOfertaCodigo)
      ]).then(([planos, licencas]) => {
        setPlanosEmail(maplicencasAdicionaisPorDireitoParaGerenciadorDeLicencas(planos || []));
        setLicencasOpcionais(mapPlanosPorDireitoParaGerenciadorDeLicencas(licencas || []));
        setDadosLicencasCarregados(true); 
      }).catch(error =>{
        NotificationManager.error(error.response?.data?.erros[0] ?? MENSAGEM_ERRO_DADOS_NAO_OBTIDOS, 'Erro!', MessagesDefaultTimeout.Error);
        setDadosLicencasCarregados(true); 
      })
    };
    carregarDadosParaCalculo();
  }, []);

  useEffect(() => {   
    const gerarInputs = () => {
      const inputs = [
        createSelectInput(CAMPO_PLANO_EMAIL, "Plano de E-mail", { 
          callback: () => Promise.resolve(planosEmail), 
          loadingType: LoadingType.AoRenderizar, 
          idField: "value",
          autoFillWhenSingleValue: false
        }, {}, {colSpan: 12}),
        createSelectGroupInput(CAMPO_LICENCAS_OPCIONAIS, "Licenças Opcionais", { 
          callback: () => Promise.resolve(licencasOpcionais), 
          loadingType: LoadingType.AoRenderizar, 
          idField: "value",
          autoFillWhenSingleValue: false
        }, {required: false}, {colSpan: 12})
      ];
      setInputsData(inputs);
    };

    if (planosEmail.length > 0 || licencasOpcionais.length > 0) {
      gerarInputs();
    }
  }, [dadosLicencasCarregados]);

  useEffect(()=>{
    setPlanoSelecionado(planoInicial)
  }, [planoInicial] )

  const alterarStatusDoPlano = (status, mensagem) => {
    const alterador = {
      sucesso: FormularioUtils.alterarStatusDoCampoParaSucesso,
      validando: FormularioUtils.alterarStatusDoCampoParaValidando,
      erro: FormularioUtils.alterarStatusDoCampoParaErro,
    }[status];
    if (alterador) {
      setInputsData(prevInputs => alterador(prevInputs, CAMPO_PLANO_EMAIL, mensagem));
    }
  };

  const handlePlanoChange = async (planoId) => {
    setPlanoSelecionado(planoId);
    setPlanoDisponivel(false);
    if (!planoId) return;
    const validacaoDisponibilidadePlanoDTO = {
      tenantCode: direito.tenantCode,
      produtoPacoteId: planoId,
      quantidadeSolicitada: quantidadePlanoSolicitada
    }
    alterarStatusDoPlano('validando', 'Validando disponibilidade do plano...');
    await ProdutosService.ValidarDisponibilidadeDaLicencaPorPacote(validacaoDisponibilidadePlanoDTO).then(()=>{
      alterarStatusDoPlano('sucesso', 'Licença disponível!');
      setPlanoDisponivel(true);
    }).catch(error =>{
      alterarStatusDoPlano('erro', error.response?.data?.erros[0] ?? MENSAGEM_ERRO_VALIDACAO);
    })
  };

  const handleLicencasOpcionaisChange = (selecionadas) => {
    const todasAsLicencas = licencasOpcionais.map(l => l.value);
    const novasLicencas = selecionadas.includes("selecionar_todos") ? todasAsLicencas : selecionadas;
    setLicencasSelecionadas(novasLicencas);
  };

  const onValuesChange = (changedValues) => {
    if (changedValues.hasOwnProperty(CAMPO_PLANO_EMAIL)) {
      handlePlanoChange(changedValues[CAMPO_PLANO_EMAIL]);
    }
    if (changedValues.hasOwnProperty(CAMPO_LICENCAS_OPCIONAIS)) {
      handleLicencasOpcionaisChange(changedValues[CAMPO_LICENCAS_OPCIONAIS]);
    }
  };

  const valorPlanoSelecionado = planosEmail.find(p => p.value === planoSelecionado)?.valorVenda || 0;
  const valorLicencasSelecionadas = licencasOpcionais
    .filter(l => licencasSelecionadas.includes(l.value))
    .reduce((acc, curr) => acc + (curr.valorVenda || 0), 0);
  const valorTotalLicencas = (valorPlanoSelecionado + valorLicencasSelecionadas) * quantidadePlanoSolicitada;
  const initialValues = { [CAMPO_PLANO_EMAIL]: planoSelecionado, [CAMPO_LICENCAS_OPCIONAIS]: licencasSelecionadas };

  return {
    gerenciadorProps: {
      inputsData,
      formReference,
      initialValues,
      valorPlanoSelecionado,
      valorLicencasSelecionadas,
      valorTotalLicencas,
      onValuesChange,
    },
    estado: {
        planoSelecionado,
        licencasSelecionadas,
        planoDisponivel,
        valorTotal: valorTotalLicencas,
        planos: planosEmail,
        licencasAdicionais: licencasOpcionais
    }
  };
};